import { useEffect, useState, useCallback } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, ChevronLeft, ChevronRight } from "lucide-react";

export default function Lightbox({ images = [], startIndex = 0, open, onClose }) {
  const [index, setIndex] = useState(startIndex);

  useEffect(() => { if (open) setIndex(startIndex); }, [open, startIndex]);

  const prev = useCallback(() => setIndex((i) => (i - 1 + images.length) % images.length), [images.length]);
  const next = useCallback(() => setIndex((i) => (i + 1) % images.length), [images.length]);

  useEffect(() => {
    if (!open) return;
    const onKey = (e) => {
      if (e.key === "Escape") onClose();
      else if (e.key === "ArrowLeft") prev();
      else if (e.key === "ArrowRight") next();
    };
    document.addEventListener("keydown", onKey);
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", onKey);
      document.body.style.overflow = "";
    };
  }, [open, onClose, prev, next]);

  if (!images.length) return null;
  const current = images[index];

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.3 }}
          className="fixed inset-0 z-[100] bg-fg/95 backdrop-blur-sm flex flex-col"
          data-testid="lightbox"
          onClick={onClose}
        >
          {/* Header */}
          <div className="flex items-center justify-between px-6 md:px-10 py-5 text-bg" onClick={(e) => e.stopPropagation()}>
            <div className="font-mono text-[10px] tracking-[0.24em] uppercase opacity-70">
              § {String(index + 1).padStart(2, "0")} / {String(images.length).padStart(2, "0")}
            </div>
            <button
              type="button"
              onClick={onClose}
              aria-label="Fermer"
              data-testid="lightbox-close"
              className="w-10 h-10 border border-bg/40 flex items-center justify-center hover:bg-cinabre hover:border-cinabre transition-colors"
            >
              <X size={16} strokeWidth={1.4} />
            </button>
          </div>

          {/* Image + arrows */}
          <div className="flex-1 flex items-center justify-center px-4 md:px-16 pb-4" onClick={(e) => e.stopPropagation()}>
            {images.length > 1 && (
              <button
                type="button"
                onClick={prev}
                aria-label="Précédent"
                data-testid="lightbox-prev"
                className="hidden md:flex w-12 h-12 border border-bg/40 items-center justify-center text-bg mr-6 hover:bg-cinabre hover:border-cinabre transition-colors shrink-0"
              >
                <ChevronLeft size={18} strokeWidth={1.4} />
              </button>
            )}

            <motion.figure
              key={index}
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.35 }}
              className="max-w-[1400px] w-full h-full flex flex-col items-center justify-center gap-6"
            >
              <img
                src={current.url}
                alt={current.caption || ""}
                className="max-h-[72vh] w-auto max-w-full object-contain"
              />
              {current.caption && (
                <figcaption className="text-bg/85 text-center max-w-2xl">
                  <div className="font-serif italic text-xl md:text-2xl leading-tight">{current.caption}</div>
                </figcaption>
              )}
            </motion.figure>

            {images.length > 1 && (
              <button
                type="button"
                onClick={next}
                aria-label="Suivant"
                data-testid="lightbox-next"
                className="hidden md:flex w-12 h-12 border border-bg/40 items-center justify-center text-bg ml-6 hover:bg-cinabre hover:border-cinabre transition-colors shrink-0"
              >
                <ChevronRight size={18} strokeWidth={1.4} />
              </button>
            )}
          </div>

          {/* Mobile arrows */}
          {images.length > 1 && (
            <div className="md:hidden flex items-center justify-center gap-4 pb-8 text-bg" onClick={(e) => e.stopPropagation()}>
              <button
                type="button"
                onClick={prev}
                aria-label="Précédent"
                className="w-12 h-12 border border-bg/40 flex items-center justify-center hover:bg-cinabre hover:border-cinabre transition-colors"
              >
                <ChevronLeft size={18} strokeWidth={1.4} />
              </button>
              <button
                type="button"
                onClick={next}
                aria-label="Suivant"
                className="w-12 h-12 border border-bg/40 flex items-center justify-center hover:bg-cinabre hover:border-cinabre transition-colors"
              >
                <ChevronRight size={18} strokeWidth={1.4} />
              </button>
            </div>
          )}
        </motion.div>
      )}
    </AnimatePresence>
  );
}
